import {
  type Color,
  HistoryOutlineIcon,
  PauseOutlineIcon,
  PlayIcon,
} from '@carboncopyco/icons';
import {
  ColorHex,
  type Colors,
  TextSectionHeader,
  ToggleButton,
  TooltipBubble,
} from '@carboncopyco/ui';
import { parseTime } from '@carboncopyco/utils';
import { useBoolean, useControllableValue } from 'ahooks';
import type React from 'react';
import { useEffect, useMemo } from 'react';

interface StopwatchProps {
  onClick?: (time: string, isStart: boolean) => void;
  value: number;
  onChange: React.Dispatch<React.SetStateAction<number>>;
  shouldRunTimer?: boolean;
  noDrag?: boolean;
  variant?: variant;
}

type variant = 'light' | 'dark';

const theme = {
  iconColor: {
    light: ColorHex.natural600,
    dark: undefined,
  } as Record<variant, Color>,
  textColor: {
    light: 'text-ds-natural-600',
    dark: 'text-ds-accent3-100',
  } as Record<variant, Colors>,
  toggleButtonTheme: {
    dark: true,
    light: false,
  } as Record<variant, boolean>,
};
export const Stopwatch = ({
  onClick,
  value,
  onChange,
  shouldRunTimer = true,
  noDrag,
  variant = 'dark',
}: StopwatchProps) => {
  const [running, { toggle: setRunning }] = useBoolean(shouldRunTimer);
  const [time, setTime] = useControllableValue<number>({
    value,
    onChange,
  });
  useEffect(() => {
    const delay = 10;
    let interval = undefined;
    interval = running
      ? setInterval(() => {
          setTime((prev) => prev + delay);
        }, delay)
      : clearInterval(interval);

    return () => clearInterval(interval);
  }, [running, setTime]);

  const timeOutput = useMemo(() => {
    const hours = parseTime(time, 3600000, 24);
    const minutes = parseTime(time, 60000, 60);
    const seconds = parseTime(time, 1000, 60);
    return `${hours}:${minutes}:${seconds}`;
  }, [time]);

  const getPlayButtonTitle = () => {
    if (running) {
      return 'Pause timer';
    }
    if (time === 0) {
      return 'Start timer';
    }
    return 'Resume timer';
  };

  return (
    <div className="stopwatch flex items-center justify-center w-full h-full bg-inherit select-none gap-1.5 pl-1.5">
      <div className="w-[104px]">
        <TextSectionHeader
          label={timeOutput}
          color={theme.textColor[variant]}
        />
      </div>
      <div className="flex items-center justify-start gap-1.5">
        <TooltipBubble
          placement="bottom"
          title={getPlayButtonTitle()}
        >
          <ToggleButton
            ariaLabel="toggle stopwatch start"
            dark={theme.toggleButtonTheme[variant]}
            onSelect={() => {
              setRunning();
              onClick?.(timeOutput, running);
            }}
            icon={
              running ? (
                <PauseOutlineIcon
                  size={20}
                  color={theme.iconColor[variant]}
                />
              ) : (
                <PlayIcon
                  size={20}
                  color={theme.iconColor[variant]}
                />
              )
            }
            noDrag={noDrag}
          />
        </TooltipBubble>
        <TooltipBubble
          placement="bottom"
          title="Restart"
        >
          <ToggleButton
            ariaLabel="stopwatch restart"
            dark={theme.toggleButtonTheme[variant]}
            onSelect={() => setTime(0)}
            icon={
              <HistoryOutlineIcon
                size={20}
                color={theme.iconColor[variant]}
              />
            }
            noDrag={noDrag}
          />
        </TooltipBubble>
      </div>
    </div>
  );
};
